import { z } from 'zod';
import { PatientVisitDtoSchema } from './enrollments.schema.js';
// ---------------------------------------------------------------------------
// Manual schedule changes of one participation (issue #29 B6/B7,
// REQ-PAT-024/033; frozen feature
// spec/features/patients-pathways/treatment-stages.feature section B;
// approved wireframe
// spec/domains/patients-pathways/ui/manual-schedule-changes.ui.md, gate of
// 2026-10-22)
// ---------------------------------------------------------------------------
/**
 * DB-enforced actions of the append-only change log (0023 CHECK
 * `visit_schedule_log_action_valid`).
 *
 * DELIBERATE duplication of `VISIT_SCHEDULE_ACTIONS` in `@ctms/db` (same
 * decision of 2026-09-27 as `ENROLLMENT_STATUSES`): `@ctms/contracts` stays
 * free of a dependency on the schema package, and the database keeps its own
 * CHECK constraint on the same two values. If one list changes, both must
 * change.
 */
export const SCHEDULE_CHANGE_ACTIONS = ['added', 'removed'];
export const ScheduleChangeActionSchema = z.enum(SCHEDULE_CHANGE_ACTIONS);
/**
 * One row of the append-only schedule change log (`visit_schedule_log` of
 * 0023; REQ-PAT-033: kto — `userId`/`userDisplayName`, kiedy — `createdAt`,
 * co — `action` + the visit). Never edited, never deleted.
 *
 * `trialVisitId` points at Domain 2's catalog, whose rows are never
 * hard-deleted, so the visit label stays resolvable after a removal
 * (REQ-PAT-024). A #41 template-preset entry carries `presetName` INSTEAD
 * (XOR, CHECK `visit_schedule_log_catalog_xor_preset` of migration 0027) —
 * the preset name is itself the label, so it stays resolvable the same way.
 * `patientVisitId` is kept for traceability WITHOUT an FK on purpose — a B7
 * removal hard-deletes the visit row and the log must outlive it.
 */
export const ScheduleLogEntryDtoSchema = z.object({
    id: z.number().int().positive(),
    action: ScheduleChangeActionSchema,
    /** Catalog position; null on a #41 preset entry. */
    trialVisitId: z.number().int().positive().nullable(),
    /** #41 template preset name (varchar(64)); null on a catalog entry. */
    presetName: z.string().max(64).nullable(),
    patientVisitId: z.number().int().positive(),
    /** Actor — the site-staff account that made the manual change. */
    userId: z.number().int().positive(),
    /**
     * Actor display name resolved through the users cluster; null when the
     * account no longer resolves on this site (kit-destruction precedent — a
     * dangling reference must not block the log read).
     */
    userDisplayName: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
/**
 * The change-log read (`GET /patients/:patientId/enrollments/:enrollmentId/
 * schedule`): newest-first, `items` rather than a bare array
 * (`DecisionPanelDto` convention); no pagination — one enrollment's manual
 * change log is short and rendered whole inline under the visit table.
 */
export const ScheduleLogDtoSchema = z.object({
    items: z.array(ScheduleLogEntryDtoSchema),
});
/**
 * `POST …/schedule`: the catalog position the staff member picked in the add
 * dialog (REQ-PAT-024 — the schedule is a subset of the protocol catalog,
 * visits are added EXCLUSIVELY by hand). Adding the same position twice is
 * REJECTED with 409 (T5, #39 — Piotr's comment #7, 2026-10-25: a protocol
 * visit is assigned to a patient exactly once and paid exactly once;
 * SUPERSEDES the Q-PAT-011 allowance of 2026-10-22 — the counter in the old
 * dialog stops being informational and previews the block).
 */
export const AddScheduleVisitRequestSchema = z.object({
    trialVisitId: z.number().int().positive(),
});
/**
 * `POST …/schedule/assign` (#39 bulk B6): the whole multi-visit transfer of
 * the two-panel modal in ONE all-or-nothing action (REQ-PAT-024/033).
 * Positions are catalog ids (`trialVisitId`) — the same identifier the
 * single add dialog sends. The list must be non-empty; the `min(1)` is the
 * early 400 gate for an empty transfer (the service repeats it as a second
 * belt-and-suspenders guard).
 */
export const AssignScheduleVisitsRequestSchema = z.object({
    trialVisitIds: z.array(z.number().int().positive()).min(1),
});
/**
 * `POST …/schedule/unassign` (#39 bulk B7): the whole „← WSZYSTKIE
 * ZAZNACZONE" removal in ONE all-or-nothing action. Positions here are
 * already-created VISIT rows (`id`), not catalog ids — the removal acts on
 * the schedule itself. Non-empty list (the `min(1)` 400 gate again); repeats
 * are collapsed by the service (idempotent removal), so no repeat rejection
 * at the Zod boundary.
 */
export const UnassignScheduleVisitsRequestSchema = z.object({
    visitIds: z.array(z.number().int().positive()).min(1),
});
/**
 * Both bulk doors answer 200 with the affected visits in REQUEST order, in
 * the `items` envelope (`ScheduleLogDtoSchema` / `DecisionPanelDto`
 * convention); no pagination — one enrollment's bulk transfer is bounded by
 * the protocol visit count.
 */
export const ScheduleVisitsDtoSchema = z.object({
    items: z.array(PatientVisitDtoSchema),
});
//# sourceMappingURL=schedule-log.schema.js.map